import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "./Primitives";
import { CALENDLY_URL } from "../data";

const API = "/api";

const newSubmissionId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const Booking = () => {
  const [form, setForm] = useState({ name: "", email: "", business: "", project: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const startedAt = useRef(Date.now());
  const submissionId = useRef(newSubmissionId());

  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        ...form,
        websiteSource: "Aestrics",
        formSource: "Discovery Call",
        sourcePage: window.location.href,
        formStartedAt: new Date(startedAt.current).toISOString(),
        submissionId: submissionId.current,
      });
      setDone(true);
      toast.success("Got it! Now pick a time below.");
    } catch (error) {
      const message = error.response?.data?.error || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full bg-[#FAFAFA] border border-black/10 rounded-xl px-4 py-3 text-[#0A0A0A] placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#8B3A9C] focus:ring-1 focus:ring-[#8B3A9C]/30 transition";

  return (
    <section id="contact" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="max-w-2xl mb-16">
            <span className="text-sm font-medium text-[#A1A1AA] tracking-wide">Let's talk</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-light tracking-tight leading-tight">
              Book your <span className="aes-gradient-text font-medium">free discovery call</span>.
            </h2>
            <p className="mt-5 text-lg text-[#52525B] leading-relaxed">
              Tell us a little about your project, then grab a time that suits you. No pressure, no obligation.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <Reveal>
            <form
              onSubmit={submit}
              data-testid="lead-form"
              className="bg-white border border-black/5 rounded-2xl p-7 sm:p-9"
            >
              <div className="space-y-5">
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </div>

                <div>
                  <label htmlFor="lead-name" className="block text-sm font-medium mb-2">Your name</label>
                  <input
                    id="lead-name"
                    data-testid="form-name"
                    name="name"
                    autoComplete="name"
                    required
                    maxLength={120}
                    className={field}
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>

                <div>
                  <label htmlFor="lead-email" className="block text-sm font-medium mb-2">Email</label>
                  <input
                    id="lead-email"
                    data-testid="form-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    className={field}
                    placeholder="jane@business.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>

                <div>
                  <label htmlFor="lead-business" className="block text-sm font-medium mb-2">Business name</label>
                  <input
                    id="lead-business"
                    data-testid="form-business"
                    name="business"
                    autoComplete="organization"
                    maxLength={160}
                    className={field}
                    placeholder="Optional"
                    value={form.business}
                    onChange={update("business")}
                  />
                </div>

                <div>
                  <label htmlFor="lead-project" className="block text-sm font-medium mb-2">
                    Tell us about your project
                  </label>
                  <textarea
                    id="lead-project"
                    data-testid="form-project"
                    name="project"
                    maxLength={4000}
                    rows={4}
                    className={`${field} resize-none`}
                    placeholder="What are you looking to build?"
                    value={form.project}
                    onChange={update("project")}
                  />
                </div>

              </div>

              <button
                type="submit"
                disabled={submitting || done}
                data-testid="form-submit"
                className="mt-7 w-full aes-gradient text-white font-medium px-6 py-3.5 rounded-full inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {done ? (
                  <>
                    Details sent <Check className="h-4 w-4" />
                  </>
                ) : submitting ? (
                  "Sending…"
                ) : (
                  <>
                    Send details <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-[#A1A1AA] text-center">
                {done ? "Now choose a time on the calendar →" : "Step 1 of 2 — then pick a time on the calendar."}
              </p>
              <p className="mt-2 text-[11px] leading-relaxed text-[#A1A1AA] text-center">
                Your details are used only to respond to this enquiry. Protected by automated abuse detection.
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid="calendly-embed">
              <div className="px-7 pt-7 pb-2">
                <h3 className="font-display text-xl font-medium tracking-tight">Pick a time</h3>
                <p className="text-sm text-[#52525B] mt-1">Choose a slot that works for you.</p>
              </div>
              <iframe
                title="Aestrics Studio Booking"
                src={CALENDLY_URL}
                className="w-full"
                style={{ height: "640px", border: 0 }}
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
